import { motion, useInView } from "framer-motion";
import { PieChart, Pie, Cell } from "recharts";
import { useRef, useState, useEffect } from "react";
import { EVIDENCE } from "./data";

const ease = [0.16, 1, 0.3, 1];

const Donut = ({ value, label }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [cur, setCur] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const dur = 1500;
    const t0 = performance.now();
    const tick = (now) => {
      const prog = Math.min((now - t0) / dur, 1);
      const eased = 1 - Math.pow(1 - prog, 3);
      setCur(eased * value);
      if (prog < 1) requestAnimationFrame(tick);
      else setCur(value);
    };
    requestAnimationFrame(tick);
  }, [inView, value]);

  const data = [{ v: cur }, { v: 100 - cur }];
  return (
    <div ref={ref} className="flex flex-col items-center text-center">
      <div className="relative w-[150px] h-[150px]">
        <PieChart width={150} height={150}>
          <Pie data={data} dataKey="v" innerRadius={52} outerRadius={72} startAngle={90} endAngle={-270} stroke="none" isAnimationActive={false}>
            <Cell fill="#E8630F" />
            <Cell fill="rgba(251,248,242,0.14)" />
          </Pie>
        </PieChart>
        <span className="absolute inset-0 flex items-center justify-center font-serif text-4xl">{Math.round(cur)}%</span>
      </div>
      <p className="mt-4 text-sm text-bone/70 max-w-[200px]">{label}</p>
    </div>
  );
};

export const Impact = () => (
  <section id="impact" className="relative bg-pine text-bone py-24 md:py-36 overflow-hidden" data-testid="impact">
    <div className="noise-overlay" />
    <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease }}
        className="lg:col-span-7"
      >
        <p className="text-xs tracking-[0.22em] uppercase font-semibold text-terra mb-5">What evidence can reveal</p>
        <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-bone">
          Listening turns assumptions into <span className="italic text-terra">priorities</span>.
        </h2>
        <p className="mt-6 text-bone/75 md:text-lg leading-relaxed max-w-xl">
          In one recent school review, staff data made the need clear: teachers were already supporting pupils with
          additional needs, but most had not received formal inclusive-education training. That insight created a
          focused route for practical support.
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease, delay: 0.1 }}
        className="lg:col-span-5 grid grid-cols-2 gap-6 justify-items-center border-t lg:border-t-0 lg:border-l border-bone/15 pt-10 lg:pt-0 lg:pl-12"
        data-testid="evidence-charts"
      >
        {EVIDENCE.stats.map((s) => (
          <Donut key={s.label} value={s.value} label={s.label} />
        ))}
      </motion.div>
    </div>
  </section>
);
